import React, { createContext, useMemo, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';
import { CriteriaCallback, useCase, UseCaseDispatch, UseCaseState } from 'adapter/in/common/hooks/useCase';
import { MarketAppView } from 'domain/entity/market/marketApp';
import { ReadMarketApps } from 'use-case/marketApps/read/readMarketAppsUseCase';

export type MarketAppState = UseCaseState & { marketApps: Option<MarketAppView[]> };

export const MarketAppsStateContext = createContext({} as MarketAppState);
export const useMarketApps = (): MarketAppState => useContext(MarketAppsStateContext);

type MarketAppsDispatch = UseCaseDispatch & { read: CriteriaCallback; reset: () => void };

const MarketAppsDispatchContext = createContext({} as MarketAppsDispatch);
export const dispatchMarketApps = (): MarketAppsDispatch => useContext(MarketAppsDispatchContext);

type MarketAppsProviderProps = {
    readMarketApps: ReadMarketApps;
    children: ReactElement;
};
export const MarketAppsProvider = (props: MarketAppsProviderProps): ReactElement => {
    const { children, readMarketApps } = props;
    const { isLoading, data, error, setLoading, setData, setError, execute, reset } = useCase(readMarketApps, false);
    const valueMarketAppsState = useMemo(() => ({ isLoading, marketApps: data, error }), [isLoading, data, error]);
    const valueMarketAppsDispatch = useMemo(
        () => ({
            setLoading,
            setError,
            read: execute,
            reset,
        }),
        [setLoading, setData, setError, reset]
    );
    return (
        <MarketAppsStateContext.Provider value={valueMarketAppsState}>
            <MarketAppsDispatchContext.Provider value={valueMarketAppsDispatch}>{children}</MarketAppsDispatchContext.Provider>
        </MarketAppsStateContext.Provider>
    );
};
